import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Tabs,
  Tab,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Drawer,
  ListItemAvatar,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Campaign,
  PostAdd,
  Comment,
  Delete,
  Send,
  CloudUpload,
  Settings,
  ListAlt,
  Schedule,
  AddCircleOutline,
  Close,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import APIKit from '../../../config/APIKit';
import Title from '../settings/Title';
import style from '../settings/settings.module.scss';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../store/compatibility';

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function MetaAdsIntegration() {
  const dispatch = useDispatch();
  const query = useQueryParams();

  const [activeTab, setActiveTab] = useState(0);

  // Connection & Core states
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [status, setStatus] = useState(null);

  // Facebook Pages
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [selectedPermission, setSelectedPermission] = useState('');

  // Business Portfolios (Business Managers)
  const [businesses, setBusinesses] = useState([]);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');

  // Ad Accounts
  const [adAccounts, setAdAccounts] = useState([]);
  const [, setAccountsLoading] = useState(false);
  const [selectedAdAccountId, setSelectedAdAccountId] = useState('');

  // Page Feed & Scheduler
  const [posts, setPosts] = useState({ published: [], scheduled: [] });
  const [postsLoading, setPostsLoading] = useState(false);
  const [postSubTab, setPostSubTab] = useState(0);

  // Composer Form
  const [composerMsg, setComposerMsg] = useState('');
  const [composerLink, setComposerLink] = useState('');
  const [composerFile, setComposerFile] = useState(null);
  const [composerPublishType, setComposerPublishType] = useState('immediate'); // immediate | scheduled
  const [composerScheduleTime, setComposerScheduleTime] = useState('');
  const [submittingPost, setSubmittingPost] = useState(false);

  // Comments Drawer
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Ads Manager state
  const [campaigns, setCampaigns] = useState([]);
  const [adSets, setAdSets] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);

  // Dialogs for Ads Creation
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [adSetDialogOpen, setAdSetDialogOpen] = useState(false);
  const [creativeDialogOpen, setCreativeDialogOpen] = useState(false);
  const [adDialogOpen, setAdDialogOpen] = useState(false);

  // Ads Form state
  const [campaignForm, setCampaignForm] = useState({ name: '', objective: 'OUTCOMES_AWARENESS', status: 'PAUSED' });
  const [adSetForm, setAdSetForm] = useState({
    name: '',
    campaign_id: '',
    budget_type: 'daily',
    budget_amount: '10.00',
    billing_event: 'IMPRESSIONS',
    optimization_goal: 'IMPRESSIONS',
    country: 'US',
    status: 'PAUSED',
  });
  const [creativeForm, setCreativeForm] = useState({ name: '', title: '', body: '', link: '', image_hash: '' });
  const [adForm, setAdForm] = useState({ name: '', adset_id: '', creative_id: '', status: 'PAUSED' });
  const [uploadingAdImage, setUploadingAdImage] = useState(false);

  // Lead Gen (Instant forms)
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsPaging, setLeadsPaging] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [importingLeads, setImportingLeads] = useState({});

  // Dynamic Meta App Credentials states (from db configuration)
  const [configAppId, setConfigAppId] = useState('');
  const [configAppSecret, setConfigAppSecret] = useState('');
  const [configRedirectUri, setConfigRedirectUri] = useState('');
  const [configVerifyToken, setConfigVerifyToken] = useState('yoro_crm_meta_ads_verify_token');
  const [configHasSecret, setConfigHasSecret] = useState(false);
  const [configScopes, setConfigScopes] = useState([
    'ads_read',
    'ads_management',
    'business_management',
    'leads_retrieval',
    'pages_show_list',
    'pages_read_engagement',
    'pages_manage_posts',
    'pages_manage_engagement',
    'pages_manage_ads',
  ]);
  const [savingConfig, setSavingConfig] = useState(false);

  // Loaders
  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await APIKit.get('/integrations/meta-ads/status');
      if (res?.success) {
        setStatus(res.data);
        const config = res.data?.connector?.configuration;
        if (config) {
          setConfigAppId(config.appId || '');
          setConfigRedirectUri(config.redirectUri || '');
          setConfigVerifyToken(config.verifyToken || 'yoro_crm_meta_ads_verify_token');
          setConfigHasSecret(config.hasSecret || false);
          if (config.scopes) {
            setConfigScopes(config.scopes);
          }
        }
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load Meta Ads status', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const loadPages = useCallback(async (businessId = selectedBusinessId) => {
    setPagesLoading(true);
    try {
      const url = businessId 
        ? `/integrations/meta-ads/pages?business_id=${businessId}`
        : '/integrations/meta-ads/pages';
      const res = await APIKit.get(url);
      if (res?.success) {
        const pagesList = res.data || [];
        setPages(pagesList);
        if (pagesList.length > 0) {
          setSelectedPageId(pagesList[0].id);
        } else {
          setSelectedPageId('');
        }
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load Facebook Pages', severity: 'error' }));
      setPages([]);
      setSelectedPageId('');
    } finally {
      setPagesLoading(false);
    }
  }, [dispatch, selectedBusinessId]);

  const loadBusinesses = useCallback(async () => {
    setBusinessesLoading(true);
    try {
      const res = await APIKit.get('/integrations/meta-ads/businesses');
      if (res?.success) {
        const list = res.data || [];
        setBusinesses(list);
        if (list.length > 0 && !selectedBusinessId) {
          setSelectedBusinessId(list[0].id);
        }
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load Business Portfolios', severity: 'error' }));
    } finally {
      setBusinessesLoading(false);
    }
  }, [dispatch, selectedBusinessId]);

  const loadAdAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const res = await APIKit.get('/integrations/meta-ads/ad-accounts');
      if (res?.success) {
        const accounts = res.data || [];
        setAdAccounts(accounts);
        if (accounts.length > 0 && !selectedAdAccountId) {
          setSelectedAdAccountId(accounts[0].id);
        }
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load ad accounts', severity: 'error' }));
    } finally {
      setAccountsLoading(false);
    }
  }, [dispatch, selectedAdAccountId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (query.get('connected') === '1') {
      dispatch(showSnackBar({ message: 'Meta Ads connected successfully', severity: 'success' }));
      loadStatus();
    }
    const err = query.get('error');
    if (err) {
      dispatch(showSnackBar({ message: err, severity: 'error' }));
    }
  }, [dispatch, loadStatus, query]);

  useEffect(() => {
    if (status?.connected) {
      loadAdAccounts();
      loadBusinesses();
    } else {
      setAdAccounts([]);
      setBusinesses([]);
      setPages([]);
      setForms([]);
      setLeads([]);
      setSelectedPageId('');
      setSelectedAdAccountId('');
      setSelectedBusinessId('');
      setSelectedFormId('');
    }
  }, [loadAdAccounts, loadBusinesses, status?.connected]);

  useEffect(() => {
    if (status?.connected) {
      loadPages(selectedBusinessId);
    }
  }, [selectedBusinessId, status?.connected, loadPages]);

  // Page Post & Comments loading
  const loadPosts = useCallback(async () => {
    if (!selectedPageId) return;
    setPostsLoading(true);
    try {
      const res = await APIKit.get(`/integrations/meta-ads/pages/${selectedPageId}/posts`);
      if (res?.success) {
        setPosts(res.data || { published: [], scheduled: [] });
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load page posts', severity: 'error' }));
    } finally {
      setPostsLoading(false);
    }
  }, [dispatch, selectedPageId]);

  useEffect(() => {
    if (selectedPageId && activeTab === 1) {
      loadPosts();
    }
  }, [loadPosts, selectedPageId, activeTab]);

  // Comments Loading
  const loadComments = useCallback(async (postId) => {
    if (!postId) return;
    setCommentsLoading(true);
    try {
      const res = await APIKit.get(`/integrations/meta-ads/posts/${postId}/comments`);
      if (res?.success) {
        setComments(res.data || []);
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load comments', severity: 'error' }));
    } finally {
      setCommentsLoading(false);
    }
  }, [dispatch]);

  const handleOpenComments = (post) => {
    setSelectedPost(post);
    setCommentsOpen(true);
    setComments([]);
    loadComments(post.id);
  };

  // Submit post comment reply
  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedPost) return;
    setSubmittingReply(true);
    try {
      const res = await APIKit.post(`/integrations/meta-ads/posts/${selectedPost.id}/comments`, {
        message: replyMessage,
      });
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Reply posted successfully', severity: 'success' }));
        setReplyMessage('');
        loadComments(selectedPost.id);
      }
    } catch (err) {
      dispatch(showSnackBar({ message: 'Failed to post reply', severity: 'error' }));
    } finally {
      setSubmittingReply(false);
    }
  };

  // Compose Page Post
  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!selectedPageId) {
      dispatch(showSnackBar({ message: 'Please select a Facebook Page first', severity: 'warning' }));
      return;
    }
    if (!composerMsg.trim() && !composerLink.trim() && !composerFile) {
      dispatch(showSnackBar({ message: 'Please add some content or media to post', severity: 'warning' }));
      return;
    }

    setSubmittingPost(true);
    try {
      const formData = new FormData();
      formData.append('message', composerMsg);
      formData.append('link', composerLink);

      const isScheduled = composerPublishType === 'scheduled';
      formData.append('published', isScheduled ? 'false' : 'true');

      if (isScheduled) {
        if (!composerScheduleTime) {
          dispatch(showSnackBar({ message: 'Please pick a scheduled publish time', severity: 'warning' }));
          setSubmittingPost(false);
          return;
        }
        const unixTime = Math.floor(new Date(composerScheduleTime).getTime() / 1000);
        formData.append('scheduled_publish_time', unixTime);
      }

      if (composerFile) {
        formData.append('file', composerFile);
      }

      const res = await APIKit.post(`/integrations/meta-ads/pages/${selectedPageId}/posts`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res?.success) {
        dispatch(showSnackBar({
          message: isScheduled ? 'Post scheduled successfully!' : 'Post shared successfully!',
          severity: 'success',
        }));
        setComposerMsg('');
        setComposerLink('');
        setComposerFile(null);
        setComposerPublishType('immediate');
        setComposerScheduleTime('');
        loadPosts();
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to create page post';
      dispatch(showSnackBar({ message: errMsg, severity: 'error' }));
    } finally {
      setSubmittingPost(false);
    }
  };

  // Delete Post
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post from Facebook?')) return;
    try {
      const res = await APIKit.delete(`/integrations/meta-ads/posts/${postId}`);
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Post deleted successfully', severity: 'success' }));
        loadPosts();
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to delete post', severity: 'error' }));
    }
  };

  // Ads Loading
  const loadAdsData = useCallback(async () => {
    if (!selectedAdAccountId) return;
    setAdsLoading(true);
    try {
      const [campRes, adSetRes, creativeRes, adsRes] = await Promise.all([
        APIKit.get(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/campaigns`),
        APIKit.get(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/adsets`),
        APIKit.get(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/creatives`),
        APIKit.get(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/ads`),
      ]);

      if (campRes?.success) setCampaigns(campRes.data || []);
      if (adSetRes?.success) setAdSets(adSetRes.data || []);
      if (creativeRes?.success) setCreatives(creativeRes.data || []);
      if (adsRes?.success) setAds(adsRes.data || []);
    } catch (err) {
      dispatch(showSnackBar({ message: 'Failed to load Ads Manager data', severity: 'error' }));
    } finally {
      setAdsLoading(false);
    }
  }, [dispatch, selectedAdAccountId]);

  useEffect(() => {
    if (selectedAdAccountId && activeTab === 2) {
      loadAdsData();
    }
  }, [loadAdsData, selectedAdAccountId, activeTab]);

  // Create Campaign
  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (!selectedAdAccountId) {
      dispatch(showSnackBar({ message: 'Please select an Ad Account first', severity: 'warning' }));
      return;
    }
    try {
      const res = await APIKit.post(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/campaigns`, campaignForm);
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Campaign created successfully!', severity: 'success' }));
        setCampaignDialogOpen(false);
        setCampaignForm({ name: '', objective: 'OUTCOMES_AWARENESS', status: 'PAUSED' });
        loadAdsData();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create campaign';
      dispatch(showSnackBar({ message: msg, severity: 'error' }));
    }
  };

  // Create AdSet
  const handleCreateAdSet = async (e) => {
    e.preventDefault();
    if (!selectedAdAccountId) {
      dispatch(showSnackBar({ message: 'Please select an Ad Account first', severity: 'warning' }));
      return;
    }
    try {
      const budgetInCents = Math.round(parseFloat(adSetForm.budget_amount) * 100);
      const payload = {
        name: adSetForm.name,
        campaign_id: adSetForm.campaign_id,
        billing_event: adSetForm.billing_event,
        optimization_goal: adSetForm.optimization_goal,
        targeting: { geo_locations: { countries: [adSetForm.country] } },
        status: adSetForm.status,
      };

      if (adSetForm.budget_type === 'daily') {
        payload.daily_budget = budgetInCents;
      } else {
        payload.lifetime_budget = budgetInCents;
      }

      const res = await APIKit.post(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/adsets`, payload);
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Ad Set created successfully!', severity: 'success' }));
        setAdSetDialogOpen(false);
        setAdSetForm({
          name: '',
          campaign_id: '',
          budget_type: 'daily',
          budget_amount: '10.00',
          billing_event: 'IMPRESSIONS',
          optimization_goal: 'IMPRESSIONS',
          country: 'US',
          status: 'PAUSED',
        });
        loadAdsData();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create Ad Set';
      dispatch(showSnackBar({ message: msg, severity: 'error' }));
    }
  };

  // Creative Image Upload helper
  const handleCreativeImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedAdAccountId) {
      dispatch(showSnackBar({ message: 'Please select an Ad Account first', severity: 'warning' }));
      return;
    }
    setUploadingAdImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await APIKit.post(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res?.success) {
        const hash = res.data?.images?.[file.name]?.hash;
        if (hash) {
          setCreativeForm((prev) => ({ ...prev, image_hash: hash }));
          dispatch(showSnackBar({ message: 'Ad image uploaded to Meta!', severity: 'success' }));
        } else {
          throw new Error('Image hash not returned');
        }
      }
    } catch (err) {
      dispatch(showSnackBar({ message: 'Failed to upload ad image', severity: 'error' }));
    } finally {
      setUploadingAdImage(false);
    }
  };

  // Create Ad Creative
  const handleCreateCreative = async (e) => {
    e.preventDefault();
    if (!selectedAdAccountId) {
      dispatch(showSnackBar({ message: 'Please select an Ad Account first', severity: 'warning' }));
      return;
    }
    if (!selectedPageId) {
      dispatch(showSnackBar({ message: 'Please select a Facebook Page first', severity: 'warning' }));
      return;
    }
    try {
      // Build standard spec
      const spec = {
        page_id: selectedPageId,
        link_data: {
          message: creativeForm.body,
          link: creativeForm.link || 'https://facebook.com',
          name: creativeForm.title,
        },
      };
      if (creativeForm.image_hash) {
        spec.link_data.image_hash = creativeForm.image_hash;
      }

      const payload = {
        name: creativeForm.name,
        object_story_spec: spec,
      };

      const res = await APIKit.post(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/creatives`, payload);
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Ad Creative generated!', severity: 'success' }));
        setCreativeDialogOpen(false);
        setCreativeForm({ name: '', title: '', body: '', link: '', image_hash: '' });
        loadAdsData();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create Creative';
      dispatch(showSnackBar({ message: msg, severity: 'error' }));
    }
  };

  // Create Ad
  const handleCreateAd = async (e) => {
    e.preventDefault();
    if (!selectedAdAccountId) {
      dispatch(showSnackBar({ message: 'Please select an Ad Account first', severity: 'warning' }));
      return;
    }
    try {
      const res = await APIKit.post(`/integrations/meta-ads/ad-accounts/${selectedAdAccountId}/ads`, adForm);
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Ad created successfully!', severity: 'success' }));
        setAdDialogOpen(false);
        setAdForm({ name: '', adset_id: '', creative_id: '', status: 'PAUSED' });
        loadAdsData();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create ad';
      dispatch(showSnackBar({ message: msg, severity: 'error' }));
    }
  };

  // Lead Ads (Instant Forms logic)
  const loadLeadGenForms = useCallback(
    async (pageId) => {
      if (!pageId) {
        setForms([]);
        return;
      }
      setFormsLoading(true);
      try {
        const res = await APIKit.get('/integrations/meta-ads/leadgen-forms', {
          params: { page_id: pageId },
        });
        if (res?.success) setForms(res.data || []);
      } catch (e) {
        dispatch(showSnackBar({ message: 'Failed to load lead forms', severity: 'error' }));
        setForms([]);
      } finally {
        setFormsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (selectedPageId && activeTab === 3) {
      loadLeadGenForms(selectedPageId);
      setSelectedFormId('all');
      setLeads([]);
      setLeadsPaging(null);
    }
  }, [loadLeadGenForms, selectedPageId, activeTab]);

  const loadLeads = async (after) => {
    if (!selectedPageId || !selectedFormId) {
      dispatch(showSnackBar({ message: 'Select a Page and a Lead Form', severity: 'warning' }));
      return;
    }
    setLeadsLoading(true);
    try {
      const res = await APIKit.get('/integrations/meta-ads/leads', {
        params: {
          page_id: selectedPageId,
          form_id: selectedFormId,
          limit: 50,
          ...(after ? { after } : {}),
        },
      });
      if (res?.success) {
        setLeads((prev) => (after ? [...prev, ...(res.data || [])] : res.data || []));
        setLeadsPaging(res.paging || null);
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load leads from Meta', severity: 'error' }));
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleAddToEnquiries = async (lead) => {
    if (importingLeads[lead.id]) return;
    setImportingLeads((prev) => ({ ...prev, [lead.id]: true }));
    const { name, phone, email } = extractNameAndPhone(lead.field_data);
    const cleanName = name === '—' || !name ? `Meta Enquiry (${lead.id})` : name;
    const cleanPhone = phone === '—' ? '' : phone;

    // Resolve Form Name
    const formName = forms.find((f) => f.id === lead.form_id || f.id === selectedFormId)?.name || 'Meta Instant Form';

    // Format all submitted field data
    const formFieldsText = Array.isArray(lead.field_data)
      ? lead.field_data.map((f) => `${f.name || 'Field'}: ${Array.isArray(f.values) ? f.values.join(', ') : (f.values ?? '')}`).join('\n')
      : '';
    const fullFormData = `Enquiry ID: ${lead.id}\nEnquiry Form: ${formName}\nSubmission Date: ${new Date(lead.created_time).toLocaleString()}\n\nForm Fields:\n${formFieldsText}`;

    try {
      const payload = {
        client_name: cleanName,
        mobile: cleanPhone,
        whatsapp: cleanPhone,
        email: email || null,
        type: 'LEAD',
        status: 'NEW',
        source: 'Meta Ads',
        createdAt: lead.created_time,
        form_data: fullFormData,
      };

      const res = await APIKit.post('/addClient', payload);
      if (res?.status) {
        dispatch(showSnackBar({ message: 'Enquiry imported to CRM successfully!', severity: 'success' }));
      } else {
        throw new Error(res?.message || 'Failed to import enquiry');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to import enquiry';
      dispatch(showSnackBar({ message: msg, severity: 'error' }));
    } finally {
      setImportingLeads((prev) => ({ ...prev, [lead.id]: false }));
    }
  };

  const extractNameAndPhone = (fieldData) => {
    if (!Array.isArray(fieldData)) return { name: '—', phone: '—', email: '' };
    let name = '—';
    let phone = '—';
    let email = '';

    const nameField = fieldData.find((f) => {
      const n = (f.name || '').toLowerCase();
      return n.includes('full_name') || n.includes('fullname') || n === 'name' || n.includes('first_name');
    });
    if (nameField) {
      name = Array.isArray(nameField.values) ? nameField.values.join(', ') : String(nameField.values ?? '—');
    }

    const phoneField = fieldData.find((f) => {
      const n = (f.name || '').toLowerCase();
      return (
        n.includes('phone') ||
        n.includes('number') ||
        n.includes('whatsapp') ||
        n.includes('mobile') ||
        n.includes('contact')
      );
    });
    if (phoneField) {
      phone = Array.isArray(phoneField.values) ? phoneField.values.join(', ') : String(phoneField.values ?? '—');
    }

    const emailField = fieldData.find((f) => {
      const n = (f.name || '').toLowerCase();
      return n.includes('email');
    });
    if (emailField) {
      email = Array.isArray(emailField.values) ? emailField.values.join(', ') : String(emailField.values ?? '');
    }

    return { name, phone, email };
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (!configAppId || !configRedirectUri) {
      dispatch(showSnackBar({ message: 'App ID and Redirect URI are required.', severity: 'warning' }));
      return;
    }
    setSavingConfig(true);
    try {
      const res = await APIKit.post('/integrations/meta-ads/config', {
        appId: configAppId,
        appSecret: configAppSecret || undefined,
        redirectUri: configRedirectUri,
        verifyToken: configVerifyToken,
        scopes: configScopes,
      });
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Configuration saved successfully.', severity: 'success' }));
        setConfigAppSecret('');
        await loadStatus();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save configuration';
      dispatch(showSnackBar({ message: msg, severity: 'error' }));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await APIKit.post('/integrations/meta-ads/connect', {});
      const url = res?.data?.authUrl;
      if (!url) throw new Error('Missing auth URL');
      window.location.href = url;
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to start Meta Ads connection', severity: 'error' }));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await APIKit.post('/integrations/meta-ads/disconnect', {});
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Disconnected Meta Ads', severity: 'success' }));
        await loadStatus();
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to disconnect', severity: 'error' }));
    } finally {
      setDisconnecting(false);
    }
  };

  const [subscribingWebhook, setSubscribingWebhook] = useState(false);
  const handleSubscribeWebhook = async () => {
    if (!selectedPageId) {
      dispatch(showSnackBar({ message: 'Please select a Facebook Page first', severity: 'warning' }));
      return;
    }
    setSubscribingWebhook(true);
    try {
      const res = await APIKit.post(`/integrations/meta-ads/pages/${selectedPageId}/subscribe-webhook`, {
        fields: ['leadgen']
      });
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Page subscribed to webhook successfully!', severity: 'success' }));
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to subscribe page to webhook';
      dispatch(showSnackBar({ message: msg, severity: 'error' }));
    } finally {
      setSubscribingWebhook(false);
    }
  };

  const connected = Boolean(status?.connected);

  const filteredPages = useMemo(() => {
    if (!selectedPermission) return pages;
    return pages.filter(p => p.tasks && p.tasks.includes(selectedPermission));
  }, [pages, selectedPermission]);

  return (
    <div className={style.container}>
      <Title title="Meta Ads Integration" enableButton={false} />

      <Paper
        sx={{
          mt: 2,
          p: 0,
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04)',
          background: '#ffffff',
          border: '1px solid rgba(102, 126, 234, 0.1)',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', background: 'linear-gradient(90deg, #f8fafc 0%, #edf2f7 100%)', px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newTab) => setActiveTab(newTab)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 700,
                textTransform: 'none',
                minHeight: '60px',
                fontSize: '14px',
              },
            }}
          >
            <Tab icon={<Settings sx={{ fontSize: '18px' }} />} iconPosition="start" label="Connection & Setup" />
            <Tab icon={<PostAdd sx={{ fontSize: '18px' }} />} iconPosition="start" label="Posts & Scheduler" disabled={!connected} />
            <Tab icon={<Campaign sx={{ fontSize: '18px' }} />} iconPosition="start" label="Ads Manager" disabled={!connected} />
            <Tab icon={<ListAlt sx={{ fontSize: '18px' }} />} iconPosition="start" label="Course Enquiry Loader" disabled={!connected} />
          </Tabs>

          {connected && (
            <Box sx={{ display: 'flex', gap: 2, py: 1, pr: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 200 }} disabled={businessesLoading}>
                <InputLabel id="global-business-label">Business Portfolio</InputLabel>
                <Select
                  labelId="global-business-label"
                  label="Business Portfolio"
                  value={selectedBusinessId}
                  onChange={(e) => {
                    setSelectedBusinessId(e.target.value);
                    setSelectedPageId('');
                  }}
                  sx={{ background: '#ffffff', borderRadius: '10px' }}
                >
                  <MenuItem value="">
                    <em>Personal (Direct Pages)</em>
                  </MenuItem>
                  {businesses.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name || b.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }} disabled={pagesLoading}>
                <InputLabel id="global-permission-label">Page Permission</InputLabel>
                <Select
                  labelId="global-permission-label"
                  label="Page Permission"
                  value={selectedPermission}
                  onChange={(e) => {
                    setSelectedPermission(e.target.value);
                    setSelectedPageId('');
                  }}
                  sx={{ background: '#ffffff', borderRadius: '10px' }}
                >
                  <MenuItem value=""><em>All Permissions</em></MenuItem>
                  <MenuItem value="ADVERTISE">ADVERTISE</MenuItem>
                  <MenuItem value="MANAGE">MANAGE</MenuItem>
                  <MenuItem value="CREATE_CONTENT">CREATE_CONTENT</MenuItem>
                  <MenuItem value="ANALYZE">ANALYZE</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          {/* TAB 0: Setup */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Card variant="outlined" sx={{ borderRadius: '14px', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155', mb: 1 }}>
                      Meta Developer App Credentials
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      Configure your Meta developer app credentials to enable OAuth integration and lead generation webhooks.
                    </Typography>
                    
                    <form onSubmit={handleSaveConfig}>
                      <Stack spacing={3}>
                        <TextField
                          label="App ID"
                          variant="outlined"
                          fullWidth
                          required
                          value={configAppId}
                          onChange={(e) => setConfigAppId(e.target.value)}
                          placeholder="e.g. 10928374652415"
                          InputLabelProps={{ shrink: true }}
                        />
                        
                        <TextField
                          label="App Secret"
                          type="password"
                          variant="outlined"
                          fullWidth
                          value={configAppSecret}
                          onChange={(e) => setConfigAppSecret(e.target.value)}
                          placeholder={configHasSecret ? "•••••••••••••••• (Saved)" : "Enter App Secret"}
                          InputLabelProps={{ shrink: true }}
                          autoComplete="new-password"
                          helperText={configHasSecret ? "App Secret is already configured. Leave blank to keep existing secret." : "Enter your Meta Developer App Secret."}
                        />

                        <TextField
                          label="Redirect URI"
                          variant="outlined"
                          fullWidth
                          required
                          value={configRedirectUri}
                          onChange={(e) => setConfigRedirectUri(e.target.value)}
                          placeholder="e.g. http://localhost:3500/api/integrations/meta-ads/callback"
                          InputLabelProps={{ shrink: true }}
                          helperText={`Suggested callback URL: ${window.location.protocol}//${window.location.hostname}:3500/api/integrations/meta-ads/callback`}
                        />

                        <TextField
                          label="Webhook Verification Token"
                          variant="outlined"
                          fullWidth
                          required
                          value={configVerifyToken}
                          onChange={(e) => setConfigVerifyToken(e.target.value)}
                          placeholder="e.g. yoro_crm_meta_ads_verify_token"
                          InputLabelProps={{ shrink: true }}
                          helperText={`Your Webhook callback URL: ${window.location.protocol}//${window.location.hostname}:3500/api/webhooks/meta-ads`}
                        />

                        <Box sx={{ mt: 1 }}>
                          <FormLabel component="legend" sx={{ fontWeight: 700, color: '#475569', mb: 1, display: 'block' }}>
                            Requested OAuth Scopes
                          </FormLabel>
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1.5 }}>
                            Select the permissions to request during login. Deselect Page Posts or Engagement scopes if your Meta App is a "Consumer" type or does not support them yet.
                          </Typography>
                          <Grid container spacing={1}>
                            {[
                              { scope: 'ads_read', label: 'Read Ad Account metrics (ads_read)' },
                              { scope: 'ads_management', label: 'Manage Campaigns & Ads (ads_management)' },
                              { scope: 'business_management', label: 'Business Manager Access (business_management)' },
                              { scope: 'leads_retrieval', label: 'Fetch Lead Ads details (leads_retrieval)' },
                              { scope: 'pages_show_list', label: 'List Managed Pages (pages_show_list)' },
                              { scope: 'pages_read_engagement', label: 'Read Page feed & comments (pages_read_engagement)' },
                              { scope: 'pages_manage_posts', label: 'Create & Publish Page Posts (pages_manage_posts)' },
                              { scope: 'pages_manage_engagement', label: 'Reply & Moderate Page Comments (pages_manage_engagement)' },
                              { scope: 'pages_manage_ads', label: 'Manage Page Lead Ads forms (pages_manage_ads)' },
                            ].map((item) => (
                              <Grid item xs={12} sm={6} key={item.scope}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={configScopes.includes(item.scope)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setConfigScopes((prev) => [...prev, item.scope]);
                                        } else {
                                          setConfigScopes((prev) => prev.filter((s) => s !== item.scope));
                                        }
                                      }}
                                      color="primary"
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#334155' }}>
                                      {item.label}
                                    </Typography>
                                  }
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>

                        <Box display="flex" justifyContent="flex-start">
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={savingConfig}
                            sx={{
                              borderRadius: '10px',
                              textTransform: 'none',
                              fontWeight: 700,
                              px: 3,
                              py: 1,
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                              },
                            }}
                          >
                            {savingConfig ? 'Saving…' : 'Save Configuration'}
                          </Button>
                        </Box>
                      </Stack>
                    </form>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Stack spacing={3}>
                  <Card variant="outlined" sx={{ borderRadius: '14px', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155', mb: 2 }}>
                        Integration Status
                      </Typography>
                      <Stack spacing={2.5}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                            Connection State
                          </Typography>
                          {loading ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Chip
                              label={connected ? 'Connected' : 'Disconnected'}
                              size="small"
                              sx={{
                                fontWeight: 800,
                                color: '#ffffff',
                                px: 1.5,
                                py: 0.5,
                                background: connected
                                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                  : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                              }}
                            />
                          )}
                        </Box>

                        <Divider />

                        <Box display="flex" justifyContent="center">
                          {!connected ? (
                            <Button
                              variant="contained"
                              disabled={connecting || !configAppId || !configRedirectUri}
                              onClick={handleConnect}
                              fullWidth
                              sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                py: 1.2,
                                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                                boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                                },
                                '&:disabled': {
                                  background: '#e2e8f0',
                                  color: '#94a3b8',
                                  boxShadow: 'none',
                                }
                              }}
                            >
                              {connecting ? 'Connecting…' : 'Connect Meta Ads'}
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              color="error"
                              disabled={disconnecting}
                              onClick={handleDisconnect}
                              fullWidth
                              sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                py: 1.2,
                              }}
                            >
                              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                            </Button>
                          )}
                        </Box>
                        {!configAppId || !configRedirectUri ? (
                          <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                            * App ID and Redirect URI must be saved before connecting.
                          </Typography>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>

                  {connected && (
                    <Card variant="outlined" sx={{ borderRadius: '14px', background: '#f8fafc', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 700, mb: 1.5 }}>
                          Connected Meta User
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: '#3b82f6', width: 40, height: 40, fontWeight: 700 }}>
                            {status?.meta?.user?.name?.[0] || 'M'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                              {status?.meta?.user?.name || '—'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              ID: {status?.meta?.user?.id || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  {connected && (
                    <Card variant="outlined" sx={{ borderRadius: '14px', background: '#f8fafc', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 700, mb: 1.5 }}>
                          Scope & Sync Stats
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="textPrimary">
                            <strong>Connector:</strong> Meta Ads Integration
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Last Sync:</strong> {status?.connector?.last_sync ? new Date(status.connector.last_sync).toLocaleString() : 'Never'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Scopes:</strong> ads_management, pages_manage_posts, pages_manage_engagement, business_management
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}

          {/* TAB 1: Composer, Scheduler & Comments */}
          {activeTab === 1 && (
            <Stack spacing={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155' }}>
                    Post Scheduler & Share
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Compose new messages, attach media files, publish instantly, or schedule posts.
                  </Typography>
                </Box>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="post-page-label">Select Facebook Page</InputLabel>
                  <Select
                    labelId="post-page-label"
                    label="Select Facebook Page"
                    value={selectedPageId}
                    onChange={(e) => setSelectedPageId(e.target.value)}
                  >
                    {filteredPages.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name || p.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Grid container spacing={3}>
                {/* Compose Form */}
                <Grid item xs={12} md={5}>
                  <Card variant="outlined" sx={{ borderRadius: '14px', p: 1, borderColor: '#e2e8f0' }}>
                    <form onSubmit={handleCreatePost}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#334155', mb: 2 }}>
                          Compose New Post
                        </Typography>

                        <Stack spacing={2.5}>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Post Content / Message"
                            value={composerMsg}
                            onChange={(e) => setComposerMsg(e.target.value)}
                            placeholder="What would you like to share?"
                          />

                          <TextField
                            fullWidth
                            size="small"
                            label="Link URL (Optional)"
                            value={composerLink}
                            onChange={(e) => setComposerLink(e.target.value)}
                            placeholder="https://example.com"
                          />

                          <Box>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '13px', display: 'block', mb: 1 }}>
                              Attach Image Media (Optional)
                            </FormLabel>
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<CloudUpload />}
                              sx={{ textTransform: 'none', borderRadius: '10px' }}
                            >
                              {composerFile ? composerFile.name : 'Choose File'}
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => setComposerFile(e.target.files[0] || null)}
                              />
                            </Button>
                            {composerFile && (
                              <Chip
                                label="Remove"
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => setComposerFile(null)}
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>

                          <Box>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '13px', display: 'block', mb: 0.5 }}>
                              Publishing Schedule
                            </FormLabel>
                            <RadioGroup
                              row
                              value={composerPublishType}
                              onChange={(e) => setComposerPublishType(e.target.value)}
                            >
                              <FormControlLabel value="immediate" control={<Radio size="small" />} label="Publish Now" />
                              <FormControlLabel value="scheduled" control={<Radio size="small" />} label="Schedule Post" />
                            </RadioGroup>
                          </Box>

                          {composerPublishType === 'scheduled' && (
                            <TextField
                              fullWidth
                              size="small"
                              type="datetime-local"
                              label="Scheduled Publish Time"
                              InputLabelProps={{ shrink: true }}
                              value={composerScheduleTime}
                              onChange={(e) => setComposerScheduleTime(e.target.value)}
                            />
                          )}
                        </Stack>
                      </CardContent>

                      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={submittingPost}
                          startIcon={composerPublishType === 'scheduled' ? <Schedule /> : <Send />}
                          sx={{
                            borderRadius: '10px',
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 3,
                            background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                          }}
                        >
                          {submittingPost ? 'Posting…' : composerPublishType === 'scheduled' ? 'Schedule Post' : 'Publish Post'}
                        </Button>
                      </CardActions>
                    </form>
                  </Card>
                </Grid>

                {/* Feed Queue */}
                <Grid item xs={12} md={7}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={postSubTab} onChange={(e, newV) => setPostSubTab(newV)}>
                      <Tab label={`Published Feed (${posts.published?.length || 0})`} />
                      <Tab label={`Scheduled Queue (${posts.scheduled?.length || 0})`} />
                    </Tabs>
                  </Box>

                  {postsLoading ? (
                    <Box display="flex" justifyContent="center" py={8}>
                      <CircularProgress />
                    </Box>
                  ) : postSubTab === 0 ? (
                    /* Published posts */
                    <Stack spacing={2}>
                      {posts.published?.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" py={4} align="center">
                          No published feed posts found.
                        </Typography>
                      ) : (
                        posts.published.map((post) => (
                          <Card key={post.id} variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', p: 2, gap: 2 }}>
                              {post.full_picture && (
                                <Box
                                  component="img"
                                  src={post.full_picture}
                                  alt="post media"
                                  sx={{ width: 100, height: 100, borderRadius: '8px', objectFit: 'cover' }}
                                />
                              )}
                              <Box flex={1}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Published: {new Date(post.created_time).toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="textPrimary" sx={{ mt: 1, fontWeight: 600 }}>
                                  {post.message || '— No message text —'}
                                </Typography>
                                <Box display="flex" alignItems="center" gap={3} sx={{ mt: 2 }}>
                                  <Chip
                                    icon={<Comment style={{ fontSize: 16 }} />}
                                    label={`${post.comments?.summary?.total_count || 0} Comments`}
                                    size="small"
                                    onClick={() => handleOpenComments(post)}
                                    sx={{ cursor: 'pointer', fontWeight: 600 }}
                                  />
                                  <Chip
                                    label={`Likes: ${post.likes?.summary?.total_count || 0}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                              <IconButton color="error" onClick={() => handleDeletePost(post.id)}>
                                <Delete />
                              </IconButton>
                            </Box>
                          </Card>
                        ))
                      )}
                    </Stack>
                  ) : (
                    /* Scheduled posts */
                    <Stack spacing={2}>
                      {posts.scheduled?.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" py={4} align="center">
                          No scheduled posts pending.
                        </Typography>
                      ) : (
                        posts.scheduled.map((post) => (
                          <Card key={post.id} variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', p: 2, gap: 2 }}>
                              {post.full_picture && (
                                <Box
                                  component="img"
                                  src={post.full_picture}
                                  alt="scheduled media"
                                  sx={{ width: 100, height: 100, borderRadius: '8px', objectFit: 'cover' }}
                                />
                              )}
                              <Box flex={1}>
                                <Typography variant="caption" sx={{ color: '#eab308', fontWeight: 700 }} display="block">
                                  Scheduled for: {post.scheduled_publish_time ? new Date(post.scheduled_publish_time * 1000).toLocaleString() : '—'}
                                </Typography>
                                <Typography variant="body2" color="textPrimary" sx={{ mt: 1, fontWeight: 600 }}>
                                  {post.message || '— No message text —'}
                                </Typography>
                              </Box>
                              <IconButton color="error" onClick={() => handleDeletePost(post.id)}>
                                <Delete />
                              </IconButton>
                            </Box>
                          </Card>
                        ))
                      )}
                    </Stack>
                  )}
                </Grid>
              </Grid>

              {/* COMMENTS SIDE PANEL */}
              <Drawer anchor="right" open={commentsOpen} onClose={() => setCommentsOpen(false)}>
                <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Post Comments
                    </Typography>
                    <IconButton onClick={() => setCommentsOpen(false)}>
                      <Close />
                    </IconButton>
                  </Box>

                  <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
                    {commentsLoading ? (
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </Box>
                    ) : comments.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" align="center" py={4}>
                        No comments found on this post.
                      </Typography>
                    ) : (
                      <List>
                        {comments.map((comm) => (
                          <ListItem key={comm.id} alignItems="flex-start" sx={{ mb: 1.5, p: 0 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: '#a78bfa', width: 32, height: 32, fontSize: '13px' }}>
                                {comm.from?.name?.[0] || 'C'}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  {comm.from?.name || 'Anonymous User'}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="textPrimary" sx={{ my: 0.5 }}>
                                    {comm.message}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {new Date(comm.created_time).toLocaleString()}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Comment input form */}
                  <form onSubmit={handleSendReply}>
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Type comment/reply…"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Write a public comment reply..."
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={submittingReply}
                        sx={{
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                        }}
                      >
                        <Send style={{ fontSize: 18 }} />
                      </Button>
                    </Stack>
                  </form>
                </Box>
              </Drawer>
            </Stack>
          )}

          {/* TAB 2: Ads Manager */}
          {activeTab === 2 && (
            <Stack spacing={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155' }}>
                    Ads Manager
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Deploy campaigns, customize targeting ad sets, publish creatives, and launch ads.
                  </Typography>
                </Box>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="ad-acc-label">Select Ad Account</InputLabel>
                  <Select
                    labelId="ad-acc-label"
                    label="Select Ad Account"
                    value={selectedAdAccountId}
                    onChange={(e) => setSelectedAdAccountId(e.target.value)}
                  >
                    {adAccounts.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name || a.id} ({a.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box display="flex" gap={1.5} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setCampaignDialogOpen(true)}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                >
                  Create Campaign
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setAdSetDialogOpen(true)}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                >
                  Create Ad Set
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setCreativeDialogOpen(true)}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                >
                  Create Creative
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setAdDialogOpen(true)}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                >
                  Launch Ad
                </Button>
              </Box>

              {adsLoading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* Campaigns list */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#334155', mb: 1.5 }}>
                      Active Campaigns ({campaigns.length})
                    </Typography>
                    <Stack spacing={1.5}>
                      {campaigns.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" py={2}>
                          No campaigns found.
                        </Typography>
                      ) : (
                        campaigns.map((camp) => (
                          <Paper key={camp.id} variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="start">
                              <Box>
                                <Typography sx={{ fontWeight: 700 }}>{camp.name}</Typography>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  ID: {camp.id} · Obj: {camp.objective}
                                </Typography>
                              </Box>
                              <Chip
                                label={camp.status}
                                size="small"
                                color={camp.status === 'ACTIVE' ? 'success' : 'default'}
                                sx={{ fontWeight: 700 }}
                              />
                            </Box>
                          </Paper>
                        ))
                      )}
                    </Stack>
                  </Grid>

                  {/* Ads list */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#334155', mb: 1.5 }}>
                      Deployed Ads ({ads.length})
                    </Typography>
                    <Stack spacing={1.5}>
                      {ads.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" py={2}>
                          No ads found.
                        </Typography>
                      ) : (
                        ads.map((adItem) => (
                          <Paper key={adItem.id} variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="start">
                              <Box>
                                <Typography sx={{ fontWeight: 700 }}>{adItem.name}</Typography>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  ID: {adItem.id} · AdSet: {adItem.adset_id}
                                </Typography>
                              </Box>
                              <Chip
                                label={adItem.status}
                                size="small"
                                color={adItem.status === 'ACTIVE' ? 'success' : 'default'}
                                sx={{ fontWeight: 700 }}
                              />
                            </Box>
                          </Paper>
                        ))
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              )}

              {/* DIALOGS FOR CREATION */}
              {/* Campaign Dialog */}
              <Dialog open={campaignDialogOpen} onClose={() => setCampaignDialogOpen(false)} fullWidth maxWidth="xs">
                <form onSubmit={handleCreateCampaign}>
                  <DialogTitle sx={{ fontWeight: 800 }}>Create New Campaign</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        label="Campaign Name"
                        required
                        value={campaignForm.name}
                        onChange={(e) => setCampaignForm((prev) => ({ ...prev, name: e.target.value }))}
                      />
                      <FormControl fullWidth>
                        <InputLabel>Objective</InputLabel>
                        <Select
                          value={campaignForm.objective}
                          label="Objective"
                          onChange={(e) => setCampaignForm((prev) => ({ ...prev, objective: e.target.value }))}
                        >
                          <MenuItem value="OUTCOMES_AWARENESS">Brand Awareness</MenuItem>
                          <MenuItem value="OUTCOMES_TRAFFIC">Website Traffic</MenuItem>
                          <MenuItem value="OUTCOMES_ENGAGEMENT">Engagement</MenuItem>
                          <MenuItem value="OUTCOMES_LEADS">Lead Generation</MenuItem>
                          <MenuItem value="OUTCOMES_APP_PROMOTION">App Installs</MenuItem>
                          <MenuItem value="OUTCOMES_SALES">Sales / Conversions</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={campaignForm.status}
                          label="Status"
                          onChange={(e) => setCampaignForm((prev) => ({ ...prev, status: e.target.value }))}
                        >
                          <MenuItem value="ACTIVE">Active (Publish immediately)</MenuItem>
                          <MenuItem value="PAUSED">Paused (Draft mode)</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </DialogContent>
                  <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setCampaignDialogOpen(false)} sx={{ textTransform: 'none' }}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                      Create Campaign
                    </Button>
                  </DialogActions>
                </form>
              </Dialog>

              {/* AdSet Dialog */}
              <Dialog open={adSetDialogOpen} onClose={() => setAdSetDialogOpen(false)} fullWidth maxWidth="sm">
                <form onSubmit={handleCreateAdSet}>
                  <DialogTitle sx={{ fontWeight: 800 }}>Create Ad Set</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        label="Ad Set Name"
                        required
                        value={adSetForm.name}
                        onChange={(e) => setAdSetForm((prev) => ({ ...prev, name: e.target.value }))}
                      />

                      <FormControl fullWidth required>
                        <InputLabel>Campaign</InputLabel>
                        <Select
                          value={adSetForm.campaign_id}
                          label="Campaign"
                          onChange={(e) => setAdSetForm((prev) => ({ ...prev, campaign_id: e.target.value }))}
                        >
                          {campaigns.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name} ({c.id})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControl fullWidth>
                            <InputLabel>Budget Type</InputLabel>
                            <Select
                              value={adSetForm.budget_type}
                              label="Budget Type"
                              onChange={(e) => setAdSetForm((prev) => ({ ...prev, budget_type: e.target.value }))}
                            >
                              <MenuItem value="daily">Daily Budget</MenuItem>
                              <MenuItem value="lifetime">Lifetime Budget</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Amount (in USD)"
                            type="number"
                            required
                            value={adSetForm.budget_amount}
                            onChange={(e) => setAdSetForm((prev) => ({ ...prev, budget_amount: e.target.value }))}
                          />
                        </Grid>
                      </Grid>

                      <TextField
                        fullWidth
                        label="Target Country Code"
                        required
                        placeholder="e.g. US, IN, AE"
                        value={adSetForm.country}
                        onChange={(e) => setAdSetForm((prev) => ({ ...prev, country: e.target.value.toUpperCase() }))}
                      />

                      <FormControl fullWidth>
                        <InputLabel>Optimization Goal</InputLabel>
                        <Select
                          value={adSetForm.optimization_goal}
                          label="Optimization Goal"
                          onChange={(e) => setAdSetForm((prev) => ({ ...prev, optimization_goal: e.target.value }))}
                        >
                          <MenuItem value="IMPRESSIONS">Impressions</MenuItem>
                          <MenuItem value="REACH">Reach</MenuItem>
                          <MenuItem value="LINK_CLICKS">Link Clicks</MenuItem>
                          <MenuItem value="LEAD_GENERATION">Leads</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={adSetForm.status}
                          label="Status"
                          onChange={(e) => setAdSetForm((prev) => ({ ...prev, status: e.target.value }))}
                        >
                          <MenuItem value="ACTIVE">Active</MenuItem>
                          <MenuItem value="PAUSED">Paused</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </DialogContent>
                  <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setAdSetDialogOpen(false)} sx={{ textTransform: 'none' }}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                      Create Ad Set
                    </Button>
                  </DialogActions>
                </form>
              </Dialog>

              {/* Creative Dialog */}
              <Dialog open={creativeDialogOpen} onClose={() => setCreativeDialogOpen(false)} fullWidth maxWidth="sm">
                <form onSubmit={handleCreateCreative}>
                  <DialogTitle sx={{ fontWeight: 800 }}>Create Ad Creative</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        label="Creative Name"
                        required
                        value={creativeForm.name}
                        onChange={(e) => setCreativeForm((prev) => ({ ...prev, name: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        label="Headline/Title"
                        required
                        value={creativeForm.title}
                        onChange={(e) => setCreativeForm((prev) => ({ ...prev, title: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Primary Text/Body"
                        required
                        value={creativeForm.body}
                        onChange={(e) => setCreativeForm((prev) => ({ ...prev, body: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        label="Destination Link"
                        placeholder="https://example.com"
                        value={creativeForm.link}
                        onChange={(e) => setCreativeForm((prev) => ({ ...prev, link: e.target.value }))}
                      />

                      <Box>
                        <FormLabel sx={{ fontWeight: 600, fontSize: '13px', display: 'block', mb: 1 }}>
                          Upload Ad Image to Meta Account
                        </FormLabel>
                        <Button
                          component="label"
                          variant="outlined"
                          disabled={uploadingAdImage}
                          startIcon={<CloudUpload />}
                          sx={{ textTransform: 'none', borderRadius: '10px' }}
                        >
                          {uploadingAdImage ? 'Uploading…' : creativeForm.image_hash ? 'Image uploaded!' : 'Choose Image'}
                          <input type="file" hidden accept="image/*" onChange={handleCreativeImageUpload} />
                        </Button>
                        {creativeForm.image_hash && (
                          <Typography variant="caption" display="block" color="success.main" sx={{ mt: 0.5 }}>
                            Image Hash: {creativeForm.image_hash}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </DialogContent>
                  <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setCreativeDialogOpen(false)} sx={{ textTransform: 'none' }}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                      Generate Creative
                    </Button>
                  </DialogActions>
                </form>
              </Dialog>

              {/* Ad Launch Dialog */}
              <Dialog open={adDialogOpen} onClose={() => setAdDialogOpen(false)} fullWidth maxWidth="xs">
                <form onSubmit={handleCreateAd}>
                  <DialogTitle sx={{ fontWeight: 800 }}>Launch Live Ad</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        label="Ad Name"
                        required
                        value={adForm.name}
                        onChange={(e) => setAdForm((prev) => ({ ...prev, name: e.target.value }))}
                      />

                      <FormControl fullWidth required>
                        <InputLabel>Ad Set</InputLabel>
                        <Select
                          value={adForm.adset_id}
                          label="Ad Set"
                          onChange={(e) => setAdForm((prev) => ({ ...prev, adset_id: e.target.value }))}
                        >
                          {adSets.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name} ({s.id})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth required>
                        <InputLabel>Creative</InputLabel>
                        <Select
                          value={adForm.creative_id}
                          label="Creative"
                          onChange={(e) => setAdForm((prev) => ({ ...prev, creative_id: e.target.value }))}
                        >
                          {creatives.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name} ({c.id})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={adForm.status}
                          label="Status"
                          onChange={(e) => setAdForm((prev) => ({ ...prev, status: e.target.value }))}
                        >
                          <MenuItem value="ACTIVE">Active (Publish live!)</MenuItem>
                          <MenuItem value="PAUSED">Paused (Draft)</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </DialogContent>
                  <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setAdDialogOpen(false)} sx={{ textTransform: 'none' }}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="secondary" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                      Launch Ad
                    </Button>
                  </DialogActions>
                </form>
              </Dialog>
            </Stack>
          )}

          {/* TAB 3: Lead Ads */}
          {activeTab === 3 && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155' }}>
                  Course Enquiry Loader (Meta Instant Forms)
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Pick the Facebook Page and Lead Form to view synced course enquiry submissions.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                <FormControl fullWidth size="small" disabled={pagesLoading}>
                  <InputLabel id="lead-page-label">Facebook Page</InputLabel>
                  <Select
                    labelId="lead-page-label"
                    label="Facebook Page"
                    value={selectedPageId}
                    onChange={(e) => setSelectedPageId(e.target.value)}
                  >
                    {filteredPages.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name || p.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" disabled={formsLoading || !selectedPageId}>
                  <InputLabel id="lead-form-label">Enquiry Form</InputLabel>
                  <Select
                    labelId="lead-form-label"
                    label="Enquiry Form"
                    value={selectedFormId}
                    onChange={(e) => setSelectedFormId(e.target.value)}
                  >
                    <MenuItem value="all">
                      <em>All Forms</em>
                    </MenuItem>
                    {forms.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.name || f.id} {f.leads_count != null ? `(${f.leads_count} enquiries)` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  disabled={leadsLoading || !selectedPageId || !selectedFormId}
                  onClick={() => loadLeads()}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {leadsLoading ? 'Loading…' : 'Fetch Enquiries'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={subscribingWebhook || !selectedPageId}
                  onClick={handleSubscribeWebhook}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {subscribingWebhook ? 'Subscribing…' : 'Subscribe Webhook'}
                </Button>
              </Stack>

              {pagesLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="textSecondary">
                    Loading pages…
                  </Typography>
                </Box>
              ) : filteredPages.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {pages.length === 0 ? 'No pages returned. Reconnect Meta Ads with permissions.' : 'No pages match the selected permission.'}
                </Typography>
              ) : null}

              {leads.length > 0 && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', overflow: 'auto', borderColor: 'rgba(102, 126, 234, 0.2)' }}>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell align="left" sx={{ fontWeight: 700, color: '#475569', py: 1.5, whiteSpace: 'nowrap' }}>Submitted Date</TableCell>
                          <TableCell align="left" sx={{ fontWeight: 700, color: '#475569', py: 1.5, whiteSpace: 'nowrap' }}>Enquiry ID</TableCell>
                          <TableCell align="left" sx={{ fontWeight: 700, color: '#475569', py: 1.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                          <TableCell align="left" sx={{ fontWeight: 700, color: '#475569', py: 1.5, whiteSpace: 'nowrap' }}>Phone / WhatsApp</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', py: 1.5, whiteSpace: 'nowrap' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leads.map((lead) => {
                          const dateStr = lead.created_time
                            ? new Date(lead.created_time).toLocaleString()
                            : '—';
                          
                          const { name, phone } = extractNameAndPhone(lead.field_data);

                          return (
                            <TableRow key={lead.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                              <TableCell align="left" sx={{ color: '#64748b', fontSize: '13px', py: 1.5, whiteSpace: 'nowrap' }}>{dateStr}</TableCell>
                              <TableCell align="left" sx={{ color: '#64748b', fontSize: '13px', fontWeight: 500, py: 1.5, whiteSpace: 'nowrap' }}>{lead.id}</TableCell>
                              <TableCell align="left" sx={{ color: '#334155', fontSize: '13px', fontWeight: 600, py: 1.5 }}>{name}</TableCell>
                              <TableCell align="left" sx={{ color: '#334155', fontSize: '13px', py: 1.5 }}>{phone}</TableCell>
                              <TableCell align="center" sx={{ py: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setSelectedLead(lead)}
                                    sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, px: 2 }}
                                  >
                                    View Details
                                  </Button>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    disabled={importingLeads[lead.id]}
                                    onClick={() => handleAddToEnquiries(lead)}
                                    sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, px: 2 }}
                                  >
                                    {importingLeads[lead.id] ? 'Adding...' : 'Add to Enquiries'}
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {leadsPaging?.cursors?.after && (
                    <Button
                      variant="outlined"
                      disabled={leadsLoading}
                      onClick={() => loadLeads(leadsPaging.cursors.after)}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
                    >
                      Load more
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </Box>

        <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider', background: '#f8fafc' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap">
            <Typography variant="caption" color="textSecondary">
              Need help configuring settings? Ensure Facebook application OAuth callback matches your backend URI.
            </Typography>
            <Button
              component={RouterLink}
              to="/integrations/webhooks"
              variant="text"
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Manage Webhooks
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Dialog
        open={Boolean(selectedLead)}
        onClose={() => setSelectedLead(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#1e293b', pb: 1 }}>
          Enquiry Submission Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedLead && (
            <Stack spacing={2} sx={{ py: 1 }}>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                  Enquiry ID
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                  {selectedLead.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                  Enquiry Form
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                  {forms.find((f) => f.id === selectedLead.form_id || f.id === selectedFormId)?.name || 'Meta Instant Form'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                  Submitted At
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                  {selectedLead.created_time
                    ? new Date(selectedLead.created_time).toLocaleString()
                    : '—'}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                Form Data (Questions & Answers)
              </Typography>

              {Array.isArray(selectedLead.field_data) && selectedLead.field_data.length > 0 ? (
                selectedLead.field_data.map((field, idx) => {
                  const val = Array.isArray(field.values) ? field.values.join(', ') : String(field.values ?? '—');
                  const formattedField = field.name
                    ? field.name
                        .replace(/[?_-]/g, ' ')
                        .split(' ')
                        .filter(Boolean)
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ') + (field.name.endsWith('?') ? '?' : '')
                    : `Field ${idx + 1}`;

                  return (
                    <Box key={field.name || idx} sx={{ p: 1.5, backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {formattedField}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                        {val}
                      </Typography>
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No field data available for this enquiry.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={importingLeads[selectedLead?.id]}
            onClick={async () => {
              await handleAddToEnquiries(selectedLead);
              setSelectedLead(null);
            }}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
          >
            {importingLeads[selectedLead?.id] ? 'Adding...' : 'Add to Enquiries'}
          </Button>
          <Button onClick={() => setSelectedLead(null)} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
